import { useMemo } from "react";
import { formatINR, resolveFinishRateV2 } from "@/lib/quotationApi";

export default function SummaryTierTotals({ tierList, sortedTierList, data, savedId }) {
  // Big tier-total cards render highest → lowest. Finish cascade still uses the
  // configured tier order (`tierList`) so per-tier rates cascade correctly.
  const iterList = sortedTierList || tierList;

  // Distinct finishes used in this quote (first measurement per finish).
  const uniqueFinishMeasurements = useMemo(() => {
    const seen = new Map();
    (data.rooms || []).forEach((room) => {
      (room.measurements || []).forEach((m) => {
        if (!m.finish_id || !m.finish_name || seen.has(m.finish_id)) return;
        seen.set(m.finish_id, m);
      });
    });
    return Array.from(seen.values());
  }, [data.rooms]);

  // Per-tier finish rates using the cascade rule: a blank tier inherits from
  // the tier above (Economy blank → Premium price), all the way up to base.
  const finishRatesByTier = useMemo(() => {
    const map = {};
    iterList.forEach((t) => {
      map[t.id] = uniqueFinishMeasurements.map((m) => ({
        name: m.finish_name,
        rate: resolveFinishRateV2(m, t, tierList, t.finish_discount),
      }));
    });
    return map;
  }, [iterList, tierList, uniqueFinishMeasurements]);

  return (
    <div className="space-y-3 mb-5" data-testid="tier-totals">
      {iterList.map((t, idx) => {
        const isHero = idx === 0;
        const finishRates = finishRatesByTier[t.id] || [];
        return (
          <div
            key={t.id}
            className="relative overflow-hidden rounded-2xl border p-5"
            style={{
              borderColor: `${t.color}66`,
              background: `linear-gradient(135deg, ${t.color}22, rgba(255,255,255,0.01) 70%)`,
            }}
            data-testid={`tier-card-${t.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <div className="overline" style={{ color: t.color }}>
                    {t.name}
                  </div>
                </div>
                {t.description && (
                  <div className="text-[11px] text-white/55 leading-snug">
                    {t.description}
                  </div>
                )}
                {finishRates.length > 0 && (
                  <div
                    className="mt-2 flex flex-wrap gap-1.5"
                    data-testid={`tier-finish-rates-${t.id}`}
                  >
                    {finishRates.map((fr) => (
                      <span
                        key={fr.name}
                        className="text-[10px] px-1.5 py-0.5 rounded-full border inline-flex items-center gap-1"
                        style={{
                          borderColor: `${t.color}44`,
                          color: `${t.color}cc`,
                        }}
                      >
                        <span className="text-white/70">{fr.name}</span>
                        <span>{formatINR(fr.rate)}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div
                  className={`font-display font-bold leading-none ${isHero ? "text-4xl sm:text-5xl" : "text-2xl sm:text-3xl"}`}
                  style={{ color: t.color }}
                  data-testid={`tier-total-${t.id}`}
                >
                  {formatINR(t.breakdown.grand_total)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {iterList.length === 0 && (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center text-white/50 text-sm">
          No tiers selected. Go back one step to pick at least one.
        </div>
      )}
      {savedId && (
        <div className="text-[11px] text-white/40 text-right">
          Ref <span className="font-display text-[#C5A880]" data-testid="saved-ref">#{savedId.slice(0, 8)}</span>
        </div>
      )}
    </div>
  );
}
